import React from 'react';
import { supabase } from '@/lib/supabase';
import { RichTextContent } from '@/components/RichTextEditor';

interface Instruction {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export function Instructions() {
  const [instructions, setInstructions] = React.useState<Instruction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchInstructions();
  }, []);

  async function fetchInstructions() {
    try {
      const { data, error } = await supabase
        .from('producer_instructions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInstructions(data || []);
    } catch (err) {
      console.error('Error fetching instructions:', err);
      setError('Failed to load instructions');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Producer Instructions</h1>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mt-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mt-8">
        {instructions.length === 0 ? (
          <div className="text-gray-500">No instructions available yet.</div>
        ) : (
          <div className="space-y-6">
            {instructions.map((instruction) => (
              <div
                key={instruction.id}
                className="bg-white shadow rounded-lg overflow-hidden"
              >
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {instruction.title}
                  </h3>
                  <div className="prose prose-green max-w-none text-gray-600">
                    <RichTextContent content={instruction.content} />
                  </div>
                  <div className="mt-4 text-sm text-gray-400">
                    Last updated: {new Date(instruction.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 